import React, {
  ComponentClass,
  CSSProperties,
  Fragment,
  Component,
  ReactElement,
  ReactNode,
  FC
} from 'react'
import {
  AutoSizer,
  List,
  Index,
  defaultCellRangeRenderer,
  GridCellRangeProps,
  InfiniteLoader
} from 'react-virtualized'
import { MessageDescriptor } from 'react-intl'
import { css, cx } from '@emotion/css'
import { memoizeOnce } from '../../util'
import { GridScroller } from './grid-scroller'
import { BulkActionBar, bulkActionBarHeight } from './bulk-action-bar'
import { SelectionManager } from './selection-manager'
import { ScrollToTopButton } from './scroll-to-top-button'
import {
  Column,
  ColumnHeaderFilters,
  getAggregationFilters,
  MaxCellHeightsByRow
} from '.'
import { Order, OrderDirection, OrderFieldConfig } from '../order'
import {
  AggregationOptions,
  AggregationConfigs,
  Aggregations
} from '../../components/aggregations'
import { QuickFilter } from '../quick-filter'
import {
  gridHeaderHeight,
  gridHeaderDrilldownHeight,
  gridHeaderContentBelowHeight,
  gridHeaderContentFull,
  GridHeader
} from './header/grid-header'
import {
  OnGroupToggleProps,
  OnOptionToggleProps,
  OnMultiGroupToggleProps
} from '../filter'
import {
  columnHeaderHeight,
  loadingRowHeight,
  ColumnHeaderRow,
  RowContainer,
  DataRow,
  LoadingRow
} from './row'
import { ColumnHeader } from './column-header'
import { Performance } from '../../analytics'
import { HoverManager } from './hover-manager'
import { SessionMetadataField } from '../../components/session-metadata-field'

type GetCachedColumnClassnamesFn = (index: number) => undefined | string

const tableStyle = css`
  outline: none;
`

const showLastRowSpaceContainerCss = css`
  margin-top: ${bulkActionBarHeight}px;
  display: block;
`

const hideLastRowSpaceContainerCss = css`
  display: none;
`

const listContainerStyle: CSSProperties = {
  overflow: 'visible',
  pointerEvents: 'auto'
}

const defaultNumberOfHeaderRows = 2

function getDataRowIndex(index, numberOfHeaderRows) {
  return index - numberOfHeaderRows
}

interface Filters<TAggregationCategory extends string> {
  readonly filterText: string
  readonly quickFilter: string
  readonly aggregations?: AggregationOptions<TAggregationCategory>
}

export interface RowSelection<TData, TAggregationCategory extends string> {
  /**
   * If true, then unloaded items are considered selected and filters
   * should be used for actions on selected items
   */
  readonly invertedSelection: boolean

  /**
   * Items that are both loaded and selected. If an item is not yet loaded
   * it is considered selected if invertedSelection is true, but it will not
   * be in this list
   */
  readonly selectedItems: TData[]
  readonly deselectedItems: TData[]
  readonly selectedCount: number
  readonly filters: Filters<TAggregationCategory>
  readonly additionalFilters?: { readonly [key: string]: any }
}

interface GetSelectionProps {
  readonly rowGetter: (params: { readonly index: number }) => any
  readonly isRowSelected: (id: string) => boolean
}

interface GetSelectionResults<TData> {
  readonly selectedItems: TData[]
  readonly deselectedItems: TData[]
}

type GetSelections<TData> = (
  props: GetSelectionProps
) => GetSelectionResults<TData>

interface RowQueryProps<TData> {
  readonly rowDataID: string
  readonly children: (rowData: TData) => ReactNode
}

type Query<TData> =
  | ComponentClass<RowQueryProps<TData>>
  | FC<RowQueryProps<TData>>

/**
 * Subset of functions provided by react-virtualized List component ref
 */
interface ListRef {
  readonly recomputeRowHeights: (index?: number) => void
}

type ListRegistrar = (registeredChild: ListRef) => void

interface CustomRow {
  readonly height: number
}

interface CreateRowHeightProps {
  readonly indexOfLastRow: number
  readonly rowGetter: (index: Index) => any
  readonly customRow?: CustomRow
  readonly isContentFull?: boolean
}

interface HeaderExpandRow {
  readonly children: ReactNode
  readonly height: number
}

type RowHeight = (index: Index) => number

interface Props<
  TData,
  TOrderField extends string,
  TAggregationCategory extends string
> {
  readonly totalRowCount: number
  readonly loadMore: () => Promise<any>
  readonly rowHeight: (data?: TData) => number
  readonly columns: Array<Column<TData, TAggregationCategory>>
  readonly renderBulkActions?: (
    props: RowSelection<TData, TAggregationCategory>
  ) => ReactElement<any> | undefined
  readonly titleMessage?: MessageDescriptor
  readonly searchPlaceholderMessage: MessageDescriptor
  readonly order: Order<TOrderField>
  readonly onSetSortField: (field: TOrderField) => void
  readonly onSetSortDirection: (direction: OrderDirection) => void
  readonly orderFieldConfig: OrderFieldConfig<TOrderField>
  readonly defaultOrderField?: TOrderField
  readonly onFilterTextChange: (value: string) => void
  readonly onFilterTextFocus?: () => void
  readonly filterText: string
  readonly onQuickFilterIDChange?: (quickFilterID: string) => void
  readonly quickFilterID?: string
  readonly quickFilters?: QuickFilter[]
  readonly quickFilterToGroup?: (filter: QuickFilter) => string
  readonly showQuickFilter?: (filter: QuickFilter) => boolean
  readonly aggregations?: Aggregations<TAggregationCategory>
  readonly aggregationSelections?: AggregationOptions<TAggregationCategory>
  readonly aggregationConfigs?: AggregationConfigs<TAggregationCategory>
  readonly sessionMetadataFieldID?: string
  readonly sessionMetadataFields?: SessionMetadataField[]
  readonly sessionMetadataFieldToGroup?: (field: SessionMetadataField) => string
  readonly onSessionMetadataFieldIDChange?: (
    sessionMetadataFieldID: string
  ) => void
  readonly onGroupToggle?: (
    props: OnGroupToggleProps<TAggregationCategory>
  ) => void
  readonly onMultiGroupToggle?: (
    props: OnMultiGroupToggleProps<TAggregationCategory>
  ) => void
  readonly onOptionToggle?: (
    props: OnOptionToggleProps<TAggregationCategory>
  ) => void
  readonly onAggregationCategoriesClear?: (
    categories: TAggregationCategory[]
  ) => void
  readonly isRowSelectable?: (rowData: TData) => boolean
  readonly batchSize: number
  readonly loadedRowCount: number
  readonly rowGetter: (params: Index) => any
  readonly isRowLoaded: (params: Index) => boolean
  readonly rowDataValue: unknown
  readonly gridDataValue?: unknown
  readonly filterValue?: unknown
  readonly RowQuery: Query<TData>
  /**
   * Gets a reference to the underlying List component
   */
  readonly registerListRef?: (ref: ListRef) => void
  /**
   * Callback that will be invoked when the Grid component
   * determines row heights need to be recomputed
   */
  readonly recomputeRowHeights?: () => void
  readonly maxCellHeightsByRow?: MaxCellHeightsByRow
  readonly updateCellHeight?: (rowDataID: string, height: number) => void
  readonly isDrilldown?: boolean
  readonly headerDrilldownClassName?: string
  readonly hideSelectAllCheckbox?: boolean
  readonly hideColumnHeaderBorder?: boolean

  /**
   * A minimal grid is a grid that doesn't have quick filters, aggregations, row or bulk actions.
   */
  readonly isMinimalGrid?: boolean

  /**
   * Custom test ID for each grid row
   */
  readonly gridCellTestID?: string

  /**
   * If the grid is using a grid panel, this is the row ID that is open in the panel
   */
  readonly activeRowID?: string

  /**
   * If the grid is using a grid panel, this clears the active row ID
   */
  readonly clearActiveRowID?: () => void

  /**
   * If provided this callback is used to calculate the number of rows
   * that are selected when all rows have been selected or when all rows
   * have been selected followed by some number of deselections
   *
   * The callback is passed the number of rows that have been toggled
   */
  readonly getAllCount?: (props: { readonly toggled: number }) => number

  readonly headerExtraChildren?: React.ReactNode
  readonly headerExpandRow?: HeaderExpandRow
  readonly headerCenteredChildren?: React.ReactNode
  readonly headerPreOrderSelectorChildren?: React.ReactNode
  readonly renderEmptyState?: () => React.ReactNode
  readonly contentBelowHeader?: React.ReactNode
}

/* If numberOfRowsToRender is 3, that means that there are no actual rows of data in the grid.
   When the grid is empty, we should show the empty state for index 2 because the first 2
   indexes are used for the header. */
export function shouldRenderEmptyState(numberOfRowsToRender, rowIndex) {
  return numberOfRowsToRender === 3 && rowIndex === 2
}

function getHeaderHeight(
  isDrilldown?: boolean,
  contentBelowHeader?: React.ReactNode,
  isContentFull?: boolean
) {
  if (isContentFull) {
    return gridHeaderContentFull
  }

  if (contentBelowHeader) {
    return gridHeaderContentBelowHeight
  }

  if (isDrilldown) {
    return gridHeaderDrilldownHeight
  }

  return gridHeaderHeight
}

export type GridFn<
  TData,
  TOrderField extends string,
  TAggregationCategory extends string
> = (props: Props<TData, TOrderField, TAggregationCategory>) => JSX.Element

export class Grid<
  TData,
  TOrderField extends string,
  TAggregationCategory extends string = ''
> extends Component<Props<TData, TOrderField, TAggregationCategory>> {
  private cachedColumnClassNames

  private createListRegistrar = memoizeOnce(
    (listRegistrars: ListRegistrar[]) => (ref) =>
      listRegistrars.forEach((listRegistrar) => listRegistrar(ref))
  )

  private createRowGetter = memoizeOnce(
    ({ dataRowGetter, numberOfHeaderRows }) =>
      ({ index }) =>
        dataRowGetter({ index: getDataRowIndex(index, numberOfHeaderRows) })
  )

  private createIsRowLoaded = memoizeOnce(
    ({ isDataRowLoaded, numberOfHeaderRows }) =>
      ({ index }) => {
        return isDataRowLoaded({
          index: getDataRowIndex(index, numberOfHeaderRows)
        })
      }
  )

  private createComputeRowHeight = memoizeOnce<CreateRowHeightProps, RowHeight>(
    ({ rowGetter, indexOfLastRow, customRow, isContentFull }) =>
      ({ index }) => {
        const { rowHeight, isDrilldown, contentBelowHeader } = this.props

        switch (index) {
          case 0:
            return getHeaderHeight(
              isDrilldown,
              contentBelowHeader,
              isContentFull
            )
          case 1:
            return !!customRow ? customRow.height : columnHeaderHeight
          case 2:
            return !!customRow
              ? columnHeaderHeight
              : rowHeight(rowGetter({ index })) || loadingRowHeight
          case indexOfLastRow:
            return loadingRowHeight
          default:
            return rowHeight(rowGetter({ index })) || loadingRowHeight
        }
      }
  )

  public componentDidMount() {
    Performance.mark('react.gridMount')
  }

  public componentDidUpdate(prevProps) {
    if (
      prevProps.rowDataValue !== this.props.rowDataValue &&
      this.props.recomputeRowHeights
    ) {
      this.props.recomputeRowHeights()
    }
  }

  public render() {
    const {
      aggregations,
      aggregationConfigs,
      totalRowCount,
      loadMore,
      filterText,
      onAggregationCategoriesClear,
      onFilterTextChange,
      onFilterTextFocus,
      onMultiGroupToggle,
      onGroupToggle,
      onOptionToggle,
      onQuickFilterIDChange,
      isRowSelectable,
      onSetSortDirection,
      onSetSortField,
      order,
      orderFieldConfig,
      defaultOrderField,
      quickFilterID,
      quickFilters,
      RowQuery,
      searchPlaceholderMessage,
      titleMessage,
      batchSize,
      aggregationSelections,
      renderBulkActions,
      rowGetter: dataRowGetter,
      loadedRowCount,
      rowDataValue,
      gridDataValue,
      filterValue,
      isRowLoaded: isDataRowLoaded,
      updateCellHeight,
      maxCellHeightsByRow,
      registerListRef: registerListRefForRowHeightMgr,
      isDrilldown,
      headerDrilldownClassName,
      getAllCount,
      quickFilterToGroup,
      showQuickFilter,
      sessionMetadataFieldID,
      sessionMetadataFields,
      sessionMetadataFieldToGroup,
      onSessionMetadataFieldIDChange,
      headerExtraChildren,
      headerExpandRow,
      headerCenteredChildren,
      headerPreOrderSelectorChildren,
      isMinimalGrid,
      gridCellTestID,
      activeRowID,
      clearActiveRowID,
      renderEmptyState,
      contentBelowHeader
    } = this.props

    let headerExpandRowChildren
    let headerExpandRowHeight

    if (!!headerExpandRow) {
      headerExpandRowChildren = headerExpandRow.children
      headerExpandRowHeight = headerExpandRow.height
    }

    const numberOfHeaderRows = !!headerExpandRow
      ? defaultNumberOfHeaderRows + 1
      : defaultNumberOfHeaderRows

    return (
      <InfiniteLoader
        loadMoreRows={loadMore}
        rowCount={totalRowCount}
        isRowLoaded={isDataRowLoaded}
      >
        {({ registerChild, onRowsRendered }) => (
          <SelectionManager
            rowCount={totalRowCount}
            filterText={filterText}
            order={order}
            quickFilterID={quickFilterID}
            getAllCount={getAllCount}
          >
            {({
              isRowSelected,
              invertedSelection,
              selectedCount,
              allSelected,
              onSelectAllRows,
              onSelectRow,
              onClearSelection,
              selectionDataValue
            }) => {
              const isRowLoaded = this.createIsRowLoaded({
                isDataRowLoaded,
                numberOfHeaderRows
              })
              const rowGetter = this.createRowGetter({
                dataRowGetter,
                numberOfHeaderRows
              })

              const rowsRemaining = totalRowCount - loadedRowCount
              const rowsBeingLoaded =
                rowsRemaining - batchSize < 0 ? rowsRemaining : batchSize

              const isContentFull =
                !aggregationSelections?.['agentSessionId'] &&
                gridCellTestID === 'route-coverage-grid-cell' // this only applies to route coverage grid
              const headerHeight =
                columnHeaderHeight +
                getHeaderHeight(isDrilldown, contentBelowHeader, isContentFull)

              return (
                <React.Fragment>
                  <GridScroller headerHeight={headerHeight}>
                    {({
                      onScrollToTopClick,
                      showScrollToTopButton,
                      handleScroll,
                      registerListRef
                    }) => {
                      const listRegistars = [registerChild, registerListRef]
                      if (registerListRefForRowHeightMgr) {
                        listRegistars.push(registerListRefForRowHeightMgr)
                      }
                      const ref = this.createListRegistrar(listRegistars)

                      const numberOfRowsToRender =
                        loadedRowCount +
                        numberOfHeaderRows +
                        rowsBeingLoaded +
                        1

                      const indexOfLastRow = numberOfRowsToRender - 1

                      const rowHeight = this.createComputeRowHeight({
                        rowGetter,
                        indexOfLastRow,
                        customRow: !!headerExpandRowHeight
                          ? {
                              height: headerExpandRowHeight
                            }
                          : undefined,
                        isContentFull
                      })

                      const visibleColumns = this.props.columns.filter(
                        ({ isHidden }) => !isHidden || !isHidden()
                      )

                      const rowSelectionGroupTails =
                        determineRowSelectionGroupTails(
                          indexOfLastRow,
                          rowGetter,
                          isRowSelected
                        )

                      this.cachedColumnClassNames = visibleColumns.map(
                        this.getClassNameForColumn
                      )

                      return (
                        <Fragment>
                          <AutoSizer>
                            {(dims) => {
                              return (
                                <List
                                  {...dims}
                                  maxCellHeightsByRow={maxCellHeightsByRow}
                                  className={tableStyle}
                                  containerStyle={listContainerStyle}
                                  onRowsRendered={onRowsRendered}
                                  rowCount={numberOfRowsToRender}
                                  ref={ref}
                                  rowHeight={rowHeight}
                                  onScroll={handleScroll}
                                  rowDataValue={rowDataValue}
                                  gridDataValue={gridDataValue}
                                  filterValue={filterValue}
                                  order={order}
                                  quickFilterID={quickFilterID}
                                  quickFilters={quickFilters}
                                  cellRangeRenderer={this.renderCellRange}
                                  selectionDataValue={selectionDataValue}
                                  rowRenderer={({
                                    index: rowIndex,
                                    style: rowStyle
                                  }) => {
                                    if (
                                      shouldRenderEmptyState(
                                        numberOfRowsToRender,
                                        rowIndex
                                      ) &&
                                      renderEmptyState
                                    ) {
                                      return (
                                        <div style={rowStyle}>
                                          {renderEmptyState()}
                                        </div>
                                      )
                                    }

                                    if (rowIndex === 0) {
                                      return renderGridHeader({
                                        rowStyle,
                                        filterText,
                                        titleMessage,
                                        searchPlaceholderMessage,
                                        order,
                                        onSetSortField,
                                        onSetSortDirection,
                                        orderFieldConfig,
                                        defaultOrderField,
                                        onFilterTextChange,
                                        onFilterTextFocus,
                                        quickFilterID,
                                        quickFilters,
                                        quickFilterToGroup,
                                        showQuickFilter,
                                        onQuickFilterIDChange,
                                        isDrilldown,
                                        headerDrilldownClassName,
                                        sessionMetadataFieldID,
                                        sessionMetadataFields,
                                        sessionMetadataFieldToGroup,
                                        extraChildren: headerExtraChildren,
                                        onSessionMetadataFieldIDChange,
                                        headerCenteredChildren,
                                        headerPreOrderSelectorChildren,
                                        isMinimalGrid,
                                        contentBelowHeader
                                      })
                                    }

                                    const columnHeaderRow =
                                      renderColumnHeaderRow<
                                        TData,
                                        TAggregationCategory
                                      >({
                                        rowStyle,
                                        getCachedColumnClassNames:
                                          this.getCachedColumnClassNames,
                                        aggregationConfigs,
                                        aggregations,
                                        onSelectAllRows: () => {
                                          if (clearActiveRowID) {
                                            clearActiveRowID()
                                          }
                                          onSelectAllRows()
                                        },
                                        allSelected,
                                        selectedCount,
                                        aggregationSelections,
                                        filterValue,
                                        isDrilldown,
                                        visibleColumns,
                                        onMultiGroupToggle,
                                        onGroupToggle,
                                        onOptionToggle,
                                        onAggregationCategoriesClear,
                                        isMinimalGrid
                                      })

                                    if (!!headerExpandRowChildren) {
                                      if (rowIndex === 1) {
                                        return renderHeaderExpandRow({
                                          rowDataID: 'header-expand-row',
                                          rowStyle,
                                          isDrilldown,
                                          children: headerExpandRowChildren
                                        })
                                      }

                                      if (rowIndex === 2) {
                                        return columnHeaderRow
                                      }
                                    } else if (rowIndex === 1) {
                                      return columnHeaderRow
                                    }

                                    if (rowIndex === indexOfLastRow) {
                                      return renderLastRowPlaceholder({
                                        rowStyle,
                                        isDrilldown,
                                        isMinimalGrid
                                      })
                                    }

                                    if (isRowLoaded({ index: rowIndex })) {
                                      const { id: rowDataID } = rowGetter({
                                        index: rowIndex
                                      })
                                      return renderDataRow({
                                        rowDataID,
                                        rowIndex,
                                        RowQuery,
                                        getCachedColumnClassNames:
                                          this.getCachedColumnClassNames,
                                        visibleColumns,
                                        rowStyle,
                                        isRowSelectable,
                                        updateCellHeight,
                                        onSelectRow,
                                        isRowSelected,
                                        allSelected,
                                        selectedCount,
                                        gridDataValue,
                                        maxCellHeightsByRow,
                                        isDrilldown,
                                        isMinimalGrid,
                                        gridCellTestID,
                                        showShadow: rowSelectionGroupTails.some(
                                          (i) => i === rowIndex
                                        ),
                                        activeRowID,
                                        clearActiveRowID
                                      })
                                    }

                                    return renderLoadingRow({
                                      rowStyle,
                                      rowIndex,
                                      getCachedColumnClassNames:
                                        this.getCachedColumnClassNames,
                                      isDrilldown,
                                      isMinimalGrid,
                                      visibleColumns
                                    })
                                  }}
                                />
                              )
                            }}
                          </AutoSizer>

                          <ScrollToTopButton
                            bottomOffset={0}
                            show={showScrollToTopButton}
                            onClick={onScrollToTopClick}
                          />
                        </Fragment>
                      )
                    }}
                  </GridScroller>
                  <div
                    className={
                      allSelected || selectedCount > 0
                        ? showLastRowSpaceContainerCss
                        : hideLastRowSpaceContainerCss
                    }
                  />
                  {renderBulkActions && quickFilterID && (
                    <BulkActionBar
                      selectedCount={selectedCount}
                      allSelected={allSelected}
                      onClearSelection={onClearSelection}
                      children={renderBulkActions({
                        invertedSelection,
                        selectedCount,
                        filters: {
                          filterText,
                          quickFilter: quickFilterID,
                          aggregations: aggregationSelections
                        },
                        ...this.getItemSelections({
                          isRowSelected,
                          rowGetter: dataRowGetter
                        })
                      })}
                    />
                  )}
                </React.Fragment>
              )
            }}
          </SelectionManager>
        )}
      </InfiniteLoader>
    )
  }

  private getClassNameForColumn(column: Column<TData, TAggregationCategory>) {
    const { width, minWidth, maxWidth, horizontalCenter, grow = 1 } = column
    return css`
      display: flex;
      flex: ${grow} 1 ${width}px;
      overflow: hidden;
      ${minWidth && `min-width: ${minWidth}px`};
      ${maxWidth && `max-width: ${maxWidth}px`};
      ${horizontalCenter && `justify-content: center;`};
      width: 100%;
      label: GridColumn;
    `
  }

  private getCachedColumnClassNames: GetCachedColumnClassnamesFn = (
    index: number
  ) => this.cachedColumnClassNames[index]

  private getItemSelections: GetSelections<TData> = ({
    isRowSelected,
    rowGetter
  }) => {
    const { loadedRowCount } = this.props
    const selectedItems: TData[] = []
    const deselectedItems: TData[] = []

    for (let index = 0; index < loadedRowCount; index++) {
      const item = rowGetter({ index })
      if (isRowSelected(item.id)) {
        selectedItems.push(item)
      } else {
        deselectedItems.push(item)
      }
    }

    return {
      selectedItems,
      deselectedItems
    }
  }

  private renderCellRange = (props: GridCellRangeProps) => {
    const { rowStartIndex } = props
    let renderedCells = defaultCellRangeRenderer(props)

    if (rowStartIndex > 0) {
      const headerCells = defaultCellRangeRenderer({
        ...props,
        rowStartIndex: 0,
        rowStopIndex: 1
      })

      renderedCells = [
        ...headerCells.slice(0, rowStartIndex > 1 ? 2 : 1),
        ...renderedCells
      ]
    }

    return renderedCells
  }
}

export function determineRowSelectionGroupTails(
  indexOfLastRow: number,
  rowGetter: ({ index }: any) => any,
  isRowSelected: (id: string) => boolean
) {
  const tails: number[] = []
  let lastSelected = -1

  for (let rowIndex = 0; rowIndex < indexOfLastRow + 1; rowIndex++) {
    const { id } = rowGetter({ index: rowIndex })
    const isSelected = isRowSelected(id)
    if (isSelected) {
      lastSelected = rowIndex
    } else if (lastSelected > -1) {
      tails.push(lastSelected)
      lastSelected = -1
    }
  }

  if (lastSelected > -1) {
    tails.push(lastSelected)
  }
  return tails
}

function renderGridHeader({
  rowStyle,
  filterText: initialFilterText,
  titleMessage,
  searchPlaceholderMessage,
  order,
  onSetSortField,
  onSetSortDirection,
  orderFieldConfig,
  defaultOrderField,
  onFilterTextChange,
  onFilterTextFocus,
  quickFilterID,
  quickFilters,
  quickFilterToGroup,
  showQuickFilter,
  onQuickFilterIDChange,
  isDrilldown,
  headerDrilldownClassName,
  sessionMetadataFieldID,
  sessionMetadataFields,
  sessionMetadataFieldToGroup,
  extraChildren,
  onSessionMetadataFieldIDChange,
  headerCenteredChildren,
  headerPreOrderSelectorChildren,
  isMinimalGrid,
  contentBelowHeader
}) {
  return (
    <GridHeader
      style={rowStyle}
      key="header_row"
      {...{
        initialFilterText,
        titleMessage,
        searchPlaceholderMessage,
        order,
        onSetSortField,
        onSetSortDirection,
        orderFieldConfig,
        defaultOrderField,
        onFilterTextChange,
        onFilterTextFocus,
        quickFilterID,
        quickFilters,
        quickFilterToGroup,
        showQuickFilter,
        onQuickFilterIDChange,
        isDrilldown,
        headerDrilldownClassName,
        sessionMetadataFieldID,
        sessionMetadataFields,
        sessionMetadataFieldToGroup,
        extraChildren,
        onSessionMetadataFieldIDChange,
        headerCenteredChildren,
        headerPreOrderSelectorChildren,
        isMinimalGrid,
        contentBelowHeader
      }}
    />
  )
}

interface RenderColumnHeaderRowProps<
  TData,
  TAggregationCategory extends string
> {
  readonly rowStyle: React.CSSProperties | undefined
  readonly getCachedColumnClassNames: GetCachedColumnClassnamesFn
  readonly aggregationConfigs?: AggregationConfigs<TAggregationCategory>
  readonly aggregations?: Aggregations<TAggregationCategory>
  readonly onSelectAllRows: () => void
  readonly allSelected: boolean
  readonly selectedCount: number
  readonly aggregationSelections?: {}
  readonly filterValue: unknown
  readonly isDrilldown?: boolean
  readonly isMinimalGrid?: boolean
  readonly visibleColumns: Array<Column<TData, TAggregationCategory>>
  readonly onMultiGroupToggle?: (
    props: OnMultiGroupToggleProps<TAggregationCategory>
  ) => void
  readonly onGroupToggle?: (
    props: OnGroupToggleProps<TAggregationCategory>
  ) => void
  readonly onOptionToggle?: (
    props: OnOptionToggleProps<TAggregationCategory>
  ) => void
  readonly onAggregationCategoriesClear?: (
    categories: TAggregationCategory[]
  ) => void
}

function renderColumnHeaderRow<TData, TAggregationCategory extends string>({
  rowStyle,
  getCachedColumnClassNames,
  aggregationConfigs,
  aggregations,
  onSelectAllRows,
  allSelected,
  selectedCount,
  aggregationSelections,
  filterValue,
  isDrilldown = false,
  visibleColumns,
  onMultiGroupToggle,
  onGroupToggle,
  onOptionToggle,
  onAggregationCategoriesClear,
  isMinimalGrid
}: RenderColumnHeaderRowProps<TData, TAggregationCategory>) {
  const aggregationFilters = getAggregationFilters({
    aggregationConfigs,
    aggregations
  })
  return (
    <ColumnHeaderRow
      style={rowStyle}
      key="column_header_row"
      onSelect={onSelectAllRows}
      selected={allSelected}
      selectedCount={selectedCount}
      aggregations={aggregations}
      aggregationSelections={aggregationSelections}
      filterValue={filterValue}
      isDrilldown={isDrilldown}
      isMinimalGrid={isMinimalGrid}
    >
      {visibleColumns.map((column, columnIndex) => {
        const { headerConfig = {} } = column
        const { aggregations: aggregationCategories = [] } = headerConfig

        const filters: ColumnHeaderFilters<TAggregationCategory> = {
          groupConfigs: aggregationFilters,
          selections: aggregationSelections,
          groupIDs: aggregationCategories,
          onGroupToggle,
          onMultiGroupToggle,
          onOptionToggle
        }

        const clearAll = () =>
          onAggregationCategoriesClear &&
          onAggregationCategoriesClear(
            aggregationCategories as TAggregationCategory[]
          )

        return (
          <div
            className={getCachedColumnClassNames(columnIndex)}
            key={`col_header_${columnIndex}`}
          >
            <ColumnHeader
              clearAll={clearAll}
              filters={filters}
              isDrilldown={isDrilldown}
              {...headerConfig}
            />
          </div>
        )
      })}
    </ColumnHeaderRow>
  )
}

function renderLastRowPlaceholder({ rowStyle, isDrilldown, isMinimalGrid }) {
  return (
    <RowContainer
      style={rowStyle}
      key="last_row_placeholder"
      isDrilldown={isDrilldown}
      isMinimalGrid={isMinimalGrid}
    >
      <div />
    </RowContainer>
  )
}

function renderHeaderExpandRow({ rowDataID, rowStyle, isDrilldown, children }) {
  return (
    <RowContainer key={rowDataID} style={rowStyle} isDrilldown={!!isDrilldown}>
      {children}
    </RowContainer>
  )
}

const contentCellCss = css`
  cursor: default;
`

function renderDataRow({
  rowDataID,
  RowQuery,
  rowIndex,
  visibleColumns,
  rowStyle,
  getCachedColumnClassNames,
  isRowSelectable,
  updateCellHeight,
  onSelectRow,
  isRowSelected,
  allSelected,
  selectedCount,
  gridDataValue,
  maxCellHeightsByRow,
  isDrilldown,
  isMinimalGrid,
  gridCellTestID,
  showShadow,
  activeRowID,
  clearActiveRowID
}) {
  Performance.measure(
    {
      category: 'loading',
      name: 'react.gridRowRender',
      startMark: 'ng.routeChangeStart'
    },
    { limit: true }
  )

  return (
    <RowQuery rowDataID={rowDataID} key={`${rowDataID}_${rowIndex}`}>
      {(rowData) => {
        const isSelectable = !isRowSelectable || isRowSelectable(rowData)
        const isActive = activeRowID && activeRowID === rowDataID ? true : false

        return (
          <HoverManager>
            {(isHovered) => {
              return (
                <DataRow
                  isRowHovered={isHovered}
                  showShadow={showShadow}
                  style={rowStyle}
                  key={rowDataID}
                  onSelect={() => {
                    if (clearActiveRowID) {
                      clearActiveRowID()
                    }
                    onSelectRow(rowDataID)
                  }}
                  selected={isRowSelected(rowDataID) || allSelected}
                  active={isActive}
                  selectedCount={selectedCount}
                  dataValue={rowData}
                  otherDataValue={gridDataValue}
                  rowHeightValue={
                    maxCellHeightsByRow && maxCellHeightsByRow[rowDataID]
                  }
                  isSelectable={isSelectable}
                  isDrilldown={isDrilldown}
                  isMinimalGrid={isMinimalGrid}
                  gridCellTestID={gridCellTestID}
                  children={visibleColumns.map(
                    ({ cellRenderer }, columnIndex) => (
                      <DataRowCell
                        key={`row_${rowIndex}_col_${columnIndex}`}
                        rowDataID={rowDataID}
                        className={getCachedColumnClassNames(columnIndex)}
                        cellRenderer={cellRenderer}
                        rowData={rowData}
                        updateCellHeight={updateCellHeight}
                        allSelected={allSelected}
                        selectedCount={selectedCount}
                        isRowHovered={isHovered}
                      />
                    )
                  )}
                />
              )
            }}
          </HoverManager>
        )
      }}
    </RowQuery>
  )
}

function DataRowCell({
  rowDataID,
  className,
  cellRenderer,
  rowData,
  updateCellHeight,
  allSelected,
  selectedCount,
  isRowHovered
}) {
  return (
    <div className={className}>
      {cellRenderer({
        rowData,
        getCellContentProps: (props) => ({
          ...props,
          className: cx(props.className, contentCellCss)
        }),
        onCellHeightChange: (height: number) => {
          if (updateCellHeight) {
            updateCellHeight(rowDataID, height)
          }
        },
        allSelected,
        selectedCount,
        isRowHovered
      })}
    </div>
  )
}

function renderLoadingRow({
  rowStyle,
  rowIndex,
  getCachedColumnClassNames,
  isDrilldown,
  isMinimalGrid,
  visibleColumns
}) {
  return (
    <LoadingRow
      style={rowStyle}
      key={`loading_row_${rowIndex}`}
      isDrilldown={isDrilldown}
      isMinimalGrid={isMinimalGrid}
    >
      {visibleColumns.map((column, columnIndex) => (
        <div
          className={getCachedColumnClassNames(columnIndex)}
          key={`row_${rowIndex}_col_${columnIndex}`}
        >
          {column.loadingCellRenderer()}
        </div>
      ))}
    </LoadingRow>
  )
}
